import { memo } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useScope } from "@state/scopeContext.lib";
import { useUpdateClientEntityMutation } from "@api/organizationClient.api";
import { OrganizationServicesOffered } from "@common/types/organization";
import {
    SERVICE_OPTIONS,
    servicesOfferedLabels,
} from "@components/entity/constants";
import { Picker } from "@design-system/Picker/Picker.lib";
import { ClientsCloseOverviewRow } from "../clientsCloseOverviewViewConfig";

export const CloseServicesCell = memo(
    ({
        row,
    }: CellContext<
        ViewTableFeatures,
        ClientsCloseOverviewRow,
        OrganizationServicesOffered[] | null | undefined
    >) => {
        const { organizationId } = useScope();
        const { mutate: updateEntityMutate } = useUpdateClientEntityMutation();

        if (row.original.rowType !== "entity") {
            return null;
        }

        const { workspaceId, entityId } = row.original;

        if (!entityId || !organizationId || !workspaceId) {
            return null;
        }

        const selectedServices = row.original.services ?? [];
        const displayText = selectedServices
            .map((s) => servicesOfferedLabels[s])
            .join(", ");

        return (
            <ViewTableCell captureClicks>
                <Picker.Select
                    multiple
                    value={selectedServices}
                    onValueChange={(value) =>
                        updateEntityMutate({
                            organizationId,
                            clientId: workspaceId,
                            entityId,
                            payload: { services: value },
                        })
                    }
                >
                    <Picker.CellContent
                        value={displayText || undefined}
                        addText="Set services"
                        hideAddIcon
                    />
                    <Picker.Popup>
                        <Picker.List>
                            {SERVICE_OPTIONS.map((option) => (
                                <Picker.Item
                                    key={option.value}
                                    value={option.value}
                                >
                                    {option.label}
                                </Picker.Item>
                            ))}
                        </Picker.List>
                    </Picker.Popup>
                </Picker.Select>
            </ViewTableCell>
        );
    },
);

CloseServicesCell.displayName = "CloseServicesCell";
